import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { FancyMidiPlayer } from "../midi/index"; // Removed unused EVENT_TYPES
import { INSTRUMENT_URL } from "../utils/constants";
import axiosInstance from "../api/axiosInstance";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, Title, Tooltip as ChartTooltip, Legend } from "chart.js";
import { Scatter } from "react-chartjs-2"; // Import Scatter chart from react-chartjs-2
import { FancyPiano } from "../piano/index";// Import the FancyPiano class
import { Box, Typography, Button, FormControl, Select, MenuItem, Paper, Card, IconButton, Tooltip } from "@mui/material";
import './MidiPlayerPianoPage.css'; // Import our custom styles

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, Title, ChartTooltip, Legend);

// Custom plugin to draw horizontal bars and a vertical line
ChartJS.register({
  id: "horizontalBarsAndPlaybackLine",
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const playbackPosition = chart.data.playbackPosition || 0;

    ctx.save();

    // Draw horizontal bars for notes — iterate ALL datasets so every channel is drawn
    chart.data.datasets.forEach((dataset, i) => {
      // Skip datasets hidden via legend click
      if (chart.getDatasetMeta(i).hidden) return;
      const color = dataset.borderColor;
      dataset.data.forEach((dataPoint) => {
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        const xStart = xScale.getPixelForValue(dataPoint.x);
        const xEnd = xScale.getPixelForValue(dataPoint.x2);
        const y = yScale.getPixelForValue(dataPoint.y);

        ctx.beginPath();
        ctx.strokeStyle = dataPoint.color || color;
        ctx.lineWidth = 4;
        ctx.moveTo(xStart, y);
        ctx.lineTo(xEnd, y);
        ctx.stroke();
        ctx.closePath();
      });
    });

    // Draw vertical line for playback position
    const xPlayback = chart.scales.x.getPixelForValue(playbackPosition);
    ctx.beginPath();
    ctx.strokeStyle = "rgba(255, 0, 0, 0.8)"; // Red color for the playback line
    ctx.lineWidth = 2;
    ctx.moveTo(xPlayback, chart.scales.y.top);
    ctx.lineTo(xPlayback, chart.scales.y.bottom);
    ctx.stroke();
    ctx.closePath();

    ctx.restore();
  },
});

const MidiPlayerPianoPage = () => {
  const pianoKeyboardRef = useRef(null); // Ref for the <object> element
  const pianoRef = useRef(null); // Ref for the FancyPiano instance
  const midiPlayerRef = useRef(null); // Ref for the FancyMidiPlayer instance
  const chartRef = useRef(null); // Ref for the chart instance
  const chartWindowSecsRef = useRef(30); // mirror of chartWindowSecs for use inside callbacks
  const [midiPlayerInitialized, setMidiPlayerInitialized] = useState(false); // Track initialization
  const [pieces, setPieces] = useState([]);
  const [selectedPieceIndex, setSelectedPieceIndex] = useState(0); // Track the selected file
  const [noteEvents, setNoteEvents] = useState([]); // Track note events with start and end times
  const [playbackPosition, setPlaybackPosition] = useState(0); // Track the current playback position
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentTimeSecs, setCurrentTimeSecs] = useState(0);
  const [totalDurationSecs, setTotalDurationSecs] = useState(0);
  const [chartWindowSecs, setChartWindowSecs] = useState(30); // visible X-window in seconds

  // Keep the callback-accessible ref in sync with the state value
  useEffect(() => { chartWindowSecsRef.current = chartWindowSecs; }, [chartWindowSecs]);

  // Define colors for channels (memoized to prevent re-creation)
  const channelColors = useMemo(() => [
    "rgba(255, 99, 132, 1)", // Channel 1
    "rgba(54, 162, 235, 1)", // Channel 2
    "rgba(255, 206, 86, 1)", // Channel 3
    "rgba(75, 192, 192, 1)", // Channel 4
    "rgba(153, 102, 255, 1)", // Channel 5
    "rgba(255, 159, 64, 1)", // Channel 6
    "rgba(201, 203, 207, 1)", // Channel 7
    "rgba(0, 128, 128, 1)",   // Channel 8
    "rgba(128, 0, 128, 1)",   // Channel 9
    "rgba(0, 128, 0, 1)",     // Channel 10
    "rgba(128, 128, 0, 1)",   // Channel 11
    "rgba(0, 0, 128, 1)",     // Channel 12
    "rgba(128, 0, 0, 1)",     // Channel 13
    "rgba(0, 0, 0, 1)",       // Channel 14
    "rgba(192, 192, 192, 1)", // Channel 15
    "rgba(255, 0, 255, 1)",   // Channel 16
  ], []);

  // Fetch MIDI files from the server
  useEffect(() => {
    const fetchMidiFiles = async () => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        console.log("Fetching MIDI files...");
        
        const response = await axiosInstance.get('/midi-files');
        const midiFiles = response.data.map((file) => ({
          title: file.Name,
          fileName: file.Name, // Store the filename for authenticated requests
          path: `/midi-files/download/${encodeURIComponent(file.Name)}`, // Use relative path for backend download
          id: file._id
        }));
        
        setPieces(midiFiles);
        console.log("✅ MIDI files loaded:", midiFiles.length);
        
        // Auto-initialize MIDI player if files are available
        if (midiFiles.length > 0 && !midiPlayerInitialized) {
          initializeMidiPlayer();
        }
      } catch (error) {
        console.error("Error fetching MIDI files:", error);
        setErrorMessage('Failed to load MIDI files. Using local fallback files.');
        
        // Fallback to local files
        const fallbackFiles = [
          {
            title: "Chopin Etude (Local)",
            path: `${process.env.PUBLIC_URL || ''}/chopin_etude25_1.mid`,
            id: 'local-chopin'
          }
        ];
        setPieces(fallbackFiles);
        
        if (!midiPlayerInitialized) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          initializeMidiPlayer();
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMidiFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Initialize piano keyboard when DOM is ready
  useEffect(() => {
    const pianoObject = pianoKeyboardRef.current;

    if (!pianoObject) {
      console.warn("Piano keyboard object not available yet");
      return;
    }

    const initializePiano = () => {
      try {
        console.log("🎹 Piano object detected, attempting to initialize...");
        
        // Access the SVG document
        const pianoDocument = pianoObject.contentDocument || pianoObject.contentWindow?.document;
        if (!pianoDocument) {
          console.warn("Piano SVG document not accessible yet, retrying...");
          // Retry after a short delay
          setTimeout(initializePiano, 100);
          return;
        }

        // Check if the document is properly loaded
        if (pianoDocument.URL === "about:blank" || !pianoDocument.documentElement) {
          console.warn("Piano SVG document not fully loaded yet, retrying...");
          setTimeout(initializePiano, 100);
          return;
        }

        console.log("🎹 SVG Document loaded:", pianoDocument.URL);
        console.log("🎹 Document element:", pianoDocument.documentElement?.tagName);

        const piano = new FancyPiano(pianoDocument);
        pianoRef.current = piano;

        // Click-to-play: clicking any SVG key immediately plays that note
        pianoDocument.addEventListener('click', (e) => {
          const keyEl = (e.target.id?.startsWith('key-') ? e.target : null)
            ?? e.target.closest?.('[id^="key-"]');
          if (!keyEl) return;
          const noteNumber = parseInt(keyEl.id.replace('key-', ''), 10);
          if (!isNaN(noteNumber) && midiPlayerRef.current) {
            midiPlayerRef.current.playNote(noteNumber);
          }
        });

        // Subscribe piano to MIDI events if player is available
        if (midiPlayerRef.current) {
          const subscriberId = midiPlayerRef.current.subscribe(piano);
          console.log("✅ FancyPiano subscribed to MIDI events with ID:", subscriberId);
        }
      } catch (error) {
        console.error("Error initializing piano:", error);
        // Retry after a delay in case of error
        setTimeout(initializePiano, 500);
      }
    };

    // Wait for SVG to load with multiple fallback attempts
    if (pianoObject.contentDocument && pianoObject.contentDocument.URL !== "about:blank") {
      // Already loaded
      initializePiano();
    } else {
      // Set up load event listener
      const handleLoad = () => {
        console.log("🎹 Piano SVG load event fired");
        setTimeout(initializePiano, 50); // Small delay to ensure full loading
      };

      pianoObject.addEventListener('load', handleLoad);
      
      // Also try after a delay in case the event already fired
      const timeoutId = setTimeout(initializePiano, 1000);

      return () => {
        pianoObject.removeEventListener('load', handleLoad);
        clearTimeout(timeoutId);
      };
    }
  }, [midiPlayerInitialized]);

  // Define loadMidiFile first to avoid circular dependencies
  const loadMidiFile = useCallback(async (pieceIndex) => {
    const piece = pieces[pieceIndex];
    if (!piece || !midiPlayerRef.current) {
      console.warn("Cannot load MIDI file - missing piece or player");
      return;
    }

    try {
      console.log("🎼 Loading MIDI file:", piece.title);
      setIsLoading(true);
      setErrorMessage('');

      let midiUrl = piece.path;
      
      // If the path is a backend URL, fetch it with authentication and pass the arrayBuffer directly
      if (piece.path.includes('/midi-files/download/')) {
        try {
          console.log("🔐 Fetching authenticated MIDI file:", piece.fileName);
          
          // Fetch the MIDI file with authentication, handle CORS properly
          const response = await axiosInstance.get(`/midi-files/download/${encodeURIComponent(piece.fileName)}`, {
            responseType: 'arraybuffer',
            // Override withCredentials if needed for localhost
            withCredentials: window.location.hostname === 'localhost'
          });
          
          // Pass the arrayBuffer directly to the player
          await midiPlayerRef.current.setMidi(response.data);
          console.log("✅ MIDI file loaded from authenticated source");
        } catch (fetchError) {
          console.error("❌ Failed to fetch MIDI file with authentication:", fetchError);
          
          // Try without credentials as fallback for CORS issues
          try {
            console.log("🔄 Retrying MIDI download without credentials...");
            const fallbackResponse = await axiosInstance.get(`/midi-files/download/${encodeURIComponent(piece.fileName)}`, {
              responseType: 'arraybuffer',
              withCredentials: false
            });
            
            await midiPlayerRef.current.setMidi(fallbackResponse.data);
            console.log("✅ MIDI file loaded from fallback request");
          } catch (fallbackError) {
            console.error("❌ Fallback MIDI download also failed:", fallbackError);
            setErrorMessage(`Failed to load MIDI file: ${fetchError.response?.data?.message || fetchError.message}`);
            setIsLoading(false);
            return;
          }
        }
      } else {
        // For public files, use the URL directly but with authenticated fetch
        const authenticatedFetch = async (url) => {
          const response = await axiosInstance.get(url, {
            responseType: 'arraybuffer'
          });
          return response; // Return the axios response directly
        };
        
        await midiPlayerRef.current.setMidi(midiUrl, authenticatedFetch);
      }
      
      // Parse MIDI events for visualization
      const player = midiPlayerRef.current.player;
      if (player && player.tracks) {
        const events = [];
        const activeNotes = {};

        player.tracks.forEach((track) => {
          track.events.forEach((event) => {
            const ch = event.channel || 0;
            const noteKey = `${event.noteNumber}_ch${ch}`;
            if (event.name === "Note on" && event.velocity > 0) {
              activeNotes[noteKey] = { tick: event.tick, channel: ch };
            } else if (event.name === "Note off" || (event.name === "Note on" && event.velocity === 0)) {
              const activeNote = activeNotes[noteKey];
              if (activeNote) {
                events.push({
                  x:  midiPlayerRef.current.tickToSeconds(activeNote.tick),
                  x2: midiPlayerRef.current.tickToSeconds(event.tick),
                  y: event.noteNumber,
                  channel: activeNote.channel,
                  color: channelColors[activeNote.channel % 16],
                });
                delete activeNotes[noteKey];
              }
            }
          });
        });

        setNoteEvents(events);
        console.log("📊 Parsed", events.length, "note events");

        // Setup playback position tracking
        player.on("playing", (position) => {
          const posSecs = midiPlayerRef.current.tickToSeconds(position.tick);
          setPlaybackPosition(posSecs);
          setCurrentTimeSecs(posSecs);
          // Update chart axes + playback line directly (no React state cycle) for smooth scrolling
          if (chartRef.current) {
            const win = chartWindowSecsRef.current;
            const xMin = posSecs >= win / 2 ? posSecs - win / 2 : 0;
            chartRef.current.data.playbackPosition = posSecs;
            chartRef.current.options.scales.x.min = xMin;
            chartRef.current.options.scales.x.max = xMin + win;
            chartRef.current.update('none');
          }
        });
        setTotalDurationSecs(midiPlayerRef.current.getTotalDurationSecs());
      }

      console.log("✅ MIDI file loaded successfully");
    } catch (error) {
      console.error("❌ Error loading MIDI file:", error);
      setErrorMessage(`Failed to load MIDI file: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [pieces, channelColors]);

  // Auto-load first MIDI file when player is initialized and files are available
  useEffect(() => {
    const autoLoadFirstFile = async () => {
      if (midiPlayerInitialized && pieces.length > 0 && !midiPlayerRef.current?.midi) {
        console.log("🎼 Auto-loading first MIDI file for visualization...");
        await loadMidiFile(0); // Load the first file
      }
    };
    
    autoLoadFirstFile();
  }, [midiPlayerInitialized, pieces, loadMidiFile]);

  const initializeMidiPlayer = useCallback(async () => {
    if (midiPlayerInitialized) {
      console.log("MIDI Player already initialized");
      return;
    }

    try {
      console.log("🎵 Initializing MIDI Player...");
      setIsLoading(true);
      
      const player = new FancyMidiPlayer();
      midiPlayerRef.current = player;

      console.log("📥 Loading instrument from:", INSTRUMENT_URL);
      await player.loadInstrument(INSTRUMENT_URL);
      
      console.log("✅ Instrument loaded successfully");
      setMidiPlayerInitialized(true);

      // Subscribe piano if it's already initialized
      if (pianoRef.current) {
        const subscriberId = player.subscribe(pianoRef.current);
        console.log("✅ FancyPiano subscribed to MIDI events with ID:", subscriberId);
      }

      // Load initial MIDI file if available
      if (pieces.length > 0) {
        await loadMidiFile(selectedPieceIndex);
      }

    } catch (error) {
      console.error("❌ Error initializing MIDI player:", error);
      setErrorMessage(`Failed to initialize MIDI player: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [midiPlayerInitialized, pieces, selectedPieceIndex, loadMidiFile]);

  // Handle dropdown selection change
  const handleSelectionChange = async (event) => {
    const selectedIndex = event.target.value;
    
    // Handle empty state
    if (selectedIndex === '' || pieces.length === 0) {
      console.warn("Cannot select MIDI file - no files available");
      return;
    }
    
    const parsedIndex = parseInt(selectedIndex, 10);
    if (isNaN(parsedIndex) || parsedIndex < 0 || parsedIndex >= pieces.length) {
      console.warn("Invalid selection index:", selectedIndex);
      return;
    }
    
    setSelectedPieceIndex(parsedIndex);
    
    // Always stop before loading a new file - ensures clean audio/player state
    // regardless of whether isPlaying is true (pre-scheduled audio must be cancelled)
    if (midiPlayerRef.current) {
      console.log("🔄 Stopping player before loading new file:", pieces[parsedIndex]?.title);
      midiPlayerRef.current.stopMidi();
      setIsPlaying(false);
    }
    
    // Load new MIDI file
    await loadMidiFile(parsedIndex);
  };

  const handlePlay = async () => {
    if (!midiPlayerRef.current) {
      console.warn("MIDI Player not initialized");
      await initializeMidiPlayer();
      return;
    }

    // Check if a MIDI file has been loaded
    if (!midiPlayerRef.current.midi && pieces.length > 0) {
      console.log("📥 No MIDI file loaded, loading default file...");
      await loadMidiFile(selectedPieceIndex);
    }

    try {
      console.log("▶️ Starting playback...");
      await midiPlayerRef.current.playMidi();
      setIsPlaying(true);
      setErrorMessage('');
    } catch (error) {
      console.error("❌ Error starting playback:", error);
      setErrorMessage(`Playback failed: ${error.message}`);
    }
  };

  const handleStop = () => {
    if (midiPlayerRef.current) {
      console.log("⏹️ Stopping playback...");
      midiPlayerRef.current.stopMidi();
      setIsPlaying(false);
      setPlaybackPosition(0);
      setCurrentTimeSecs(0);
      if (pianoRef.current) pianoRef.current.repaintKeys();
    }
  };

  const handlePause = () => {
    if (midiPlayerRef.current) {
      console.log("⏸️ Pausing playback...");
      midiPlayerRef.current.pauseMidi();
      setIsPlaying(false);
    }
  };

  // Spacebar → play/pause toggle
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
        if (!midiPlayerInitialized || isLoading || pieces.length === 0) return;
        if (isPlaying) handlePause(); else handlePlay();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPlaying, midiPlayerInitialized, isLoading, pieces.length]);

  const handleSeek = async (deltaSecs) => {
    if (midiPlayerRef.current) {
      await midiPlayerRef.current.seekBySeconds(deltaSecs);
      if (!isPlaying) setCurrentTimeSecs(midiPlayerRef.current.getPositionSecs());
    }
  };

  const formatTime = (secs) => {
    const s = Math.floor(secs ?? 0);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  };

  // Compute the visible X window for the initial / zoomed render
  const chartXMin = currentTimeSecs >= chartWindowSecs / 2 ? currentTimeSecs - chartWindowSecs / 2 : 0;
  const chartXMax = chartXMin + chartWindowSecs;

  // Y-axis range: actual note range in this file ± 10% padding
  const noteYRange = useMemo(() => {
    if (!noteEvents.length) return { min: 21, max: 108 };
    let minY = 127, maxY = 0;
    noteEvents.forEach(e => { if (e.y < minY) minY = e.y; if (e.y > maxY) maxY = e.y; });
    const pad = Math.max(2, Math.round((maxY - minY) * 0.1));
    return { min: Math.max(0, minY - pad), max: Math.min(127, maxY + pad) };
  }, [noteEvents]);

  // Prepare data for the chart — one dataset per channel so the legend shows each channel
  const byChannel = {};
  noteEvents.forEach((event) => {
    const ch = event.channel ?? 0;
    if (!byChannel[ch]) byChannel[ch] = [];
    byChannel[ch].push(event);
  });
  const chartData = {
    datasets: Object.entries(byChannel)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([ch, events]) => ({
        label: `Ch ${Number(ch) + 1}`,
        data: events,
        borderColor: channelColors[Number(ch) % 16],
        backgroundColor: channelColors[Number(ch) % 16],
        pointRadius: 0,
        showLine: false,
      })),
    playbackPosition,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 12, padding: 12, font: { size: 11 } },
      },
      title: { display: false },
    },
    scales: {
      x: {
        type: 'linear',
        min: chartXMin,
        max: chartXMax,
        title: { display: false },
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.04)' },
        ticks: {
          callback: (val) => {
            const m = Math.floor(val / 60);
            const s = Math.floor(val % 60);
            return m > 0 ? `${m}:${String(s).padStart(2,'0')}` : `${s}s`;
          },
        },
      },
      y: {
        title: { display: false },
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.04)' },
        min: noteYRange.min,
        max: noteYRange.max,
      },
    },
    interaction: { intersect: false, mode: 'nearest' },
    animation: { duration: 0 },
  };

  return (
    <Box p={1} pt={0}>
      {/* Error Message */}
      {errorMessage && (
        <Box mb={2} p={2} bgcolor="warning.light" borderRadius={1}>
          <Typography variant="body2" color="error">
            ⚠️ {errorMessage}
          </Typography>
        </Box>
      )}

      {/* Controls */}
      <Card sx={{ mb: 1, p: 1 }}>
        <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={pieces.length > 0 ? selectedPieceIndex : ''}
              onChange={handleSelectionChange}
              disabled={isLoading}
              size="small"
            >
              {pieces.length === 0 ? (
                <MenuItem value=""><em>No MIDI files available</em></MenuItem>
              ) : (
                pieces.map((piece, index) => (
                  <MenuItem key={index} value={index}>{piece.title}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Box display="flex" gap={0.5} alignItems="center">
            <Tooltip title="Rewind 5s"><span>
              <IconButton onClick={() => handleSeek(-5)} disabled={!midiPlayerInitialized || isLoading} size="small">⏪</IconButton>
            </span></Tooltip>
            <Tooltip title={isPlaying ? "Pause" : "Play"}><span>
              <IconButton
                onClick={isPlaying ? handlePause : handlePlay}
                disabled={!midiPlayerInitialized || isLoading || pieces.length === 0}
                color="primary"
                size="large"
                sx={{ fontSize: '1.4rem' }}
              >
                {isPlaying ? '⏸' : '▶'}
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Stop"><span>
              <IconButton onClick={handleStop} disabled={!midiPlayerInitialized} color="error" size="large" sx={{ fontSize: '1.4rem' }}>⏹</IconButton>
            </span></Tooltip>
            <Tooltip title="Forward 5s"><span>
              <IconButton onClick={() => handleSeek(5)} disabled={!midiPlayerInitialized || isLoading} size="small">⏩</IconButton>
            </span></Tooltip>
          </Box>

          <Typography variant="body2" sx={{ fontFamily: 'monospace', ml: 1, minWidth: 90, color: 'text.secondary' }}>
            {formatTime(currentTimeSecs)} / {formatTime(totalDurationSecs)}
          </Typography>
          {/* Chart zoom controls */}
          <Box display="flex" alignItems="center" gap={0} sx={{ ml: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Tooltip title="Zoom in (narrower window)">
              <IconButton size="small" onClick={() => setChartWindowSecs(s => Math.max(15, Math.round(s / 2)))} sx={{ px: 0.75 }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>+</span>
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 38, textAlign: 'center', fontSize: '0.72rem' }}>
              {chartWindowSecs}s
            </Typography>
            <Tooltip title="Zoom out (wider window)">
              <IconButton size="small" onClick={() => setChartWindowSecs(s => Math.min(600, s * 2))} sx={{ px: 0.75 }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1 }}>−</span>
              </IconButton>
            </Tooltip>
          </Box>
          {!midiPlayerInitialized && (
            <Button variant="outlined" onClick={initializeMidiPlayer} disabled={isLoading} size="small">
              Initialize Player
            </Button>
          )}
        </Box>
      </Card>

      {/* Piano Visualization */}
      <Paper sx={{ mb: 1, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
        <object
          id="piano-keyboard"
          className="piano-keyboard"
          type="image/svg+xml"
          data="/piano-keyboard-mine.svg"
          ref={pianoKeyboardRef}
          style={{ width: '100%', height: '180px', display: 'block' }}
          onLoad={() => console.log("🎹 Piano SVG object loaded successfully")}
          onError={(e) => console.error("🎹 Piano SVG failed to load:", e)}
        />
      </Paper>

      {/* Note Chart */}
      <Paper sx={{ p: 1 }}>
        <Box className="note-chart-container" sx={{ height: '400px' }}>
          {noteEvents.length > 0 ? (
            <Scatter ref={chartRef} data={chartData} options={chartOptions} />
          ) : (
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="center" 
              height="100%" 
              bgcolor="grey.50" 
              borderRadius={1}
            >
              <Typography variant="body2" color="textSecondary">
                {isLoading ? "Loading MIDI data..." : "No notes to display. Load and play a MIDI file to see the visualization."}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default MidiPlayerPianoPage;